import * as React from "react";
import { useNavigate } from "react-router";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

interface YearInReviewBannerProps {
    year?: number;
}

/**
 * A banner to show in the home carousel during Year in Review season (Dec-Jan).
 * Links to the Year in Review experience.
 */
export default function YearInReviewBanner({
    year,
}: YearInReviewBannerProps): React.JSX.Element {
    const navigate = useNavigate();
    const displayYear = year ?? new Date().getFullYear();

    const handleClick = () => {
        navigate(`/stats/year-in-review/${displayYear}`);
    };

    return (
        <Box
            onClick={handleClick}
            sx={{
                cursor: "pointer",
                borderRadius: 2,
                overflow: "hidden",
                background:
                    "linear-gradient(135deg, rgb(11, 23, 48) 0%, rgb(21, 39, 86) 50%, rgb(9, 110, 229) 100%)",
                minHeight: 220,
                p: 3,
                display: "flex",
                alignItems: "center",
                position: "relative",
                "&:hover": {
                    "& .chevron": {
                        transform: "translateX(4px)",
                    },
                },
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%" }}
            >
                <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <AutoAwesomeIcon
                            sx={{
                                color: "rgb(60, 233, 124)",
                                fontSize: 28,
                            }}
                        />
                        <Typography
                            variant="h4"
                            sx={{
                                color: "rgb(60, 233, 124)",
                                fontWeight: "bold",
                                letterSpacing: 1,
                            }}
                        >
                            NEW
                        </Typography>
                    </Stack>
                    <Typography
                        variant="h2"
                        sx={{
                            color: "#fff",
                            fontSize: { xs: "1.5rem", sm: "2rem" },
                            fontWeight: "bold",
                        }}
                    >
                        {displayYear} Year in Review
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: "rgba(255,255,255,0.7)",
                            maxWidth: 250,
                        }}
                    >
                        See your training highlights, top workouts, and more!
                    </Typography>
                </Stack>

                <ChevronRightIcon
                    className="chevron"
                    sx={{
                        color: "#fff",
                        fontSize: 40,
                        opacity: 0.7,
                        transition: "transform 0.2s ease",
                    }}
                />
            </Stack>

            {/* Decorative elements */}
            <Box
                sx={{
                    position: "absolute",
                    top: -20,
                    right: -20,
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, rgba(60,233,124,0.2) 0%, transparent 70%)",
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    bottom: -30,
                    right: 60,
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, rgba(9,110,229,0.3) 0%, transparent 70%)",
                    pointerEvents: "none",
                }}
            />
        </Box>
    );
}

/**
 * Get the appropriate year for Year in Review.
 * In January, show previous year. Otherwise show current year.
 */
export function getYearInReviewYear(): number {
    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();
    // In January, show previous year's review
    return month === 0 ? year - 1 : year;
}
